import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { transactionService } from '../services/transactionService';
import Spinner from '../components/Spinner';

function TransactionsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedType, setSelectedType] = useState(
    location.state?.initialFilter || 'all'
  );

  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedMonth, setSelectedMonth] = useState('all');

  const [editingTransaction, setEditingTransaction] = useState(null);

  const months = [
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' }
  ];

  useEffect(() => {
    fetchTransactions();
    if (location.state?.initialFilter) {
      setIsFilterVisible(true);
    }
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, dateRange, selectedType, selectedMonth]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await transactionService.getAllTransactions();
      setTransactions(data);
      setFilteredTransactions(data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setError("Failed to load transactions.");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    if (selectedType !== 'all') {
      filtered = filtered.filter(t => t.type === selectedType);
    }

    if (dateRange.start && dateRange.end) {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      filtered = filtered.filter(t => {
        const transactionDate = new Date(t.date);
        return transactionDate >= startDate && transactionDate <= endDate;
      });
    }

    if (selectedMonth !== 'all') {
      filtered = filtered.filter(t => {
        const transactionMonth = new Date(t.date).getMonth();
        return transactionMonth === parseInt(selectedMonth);
      });
    }

    setFilteredTransactions(filtered);
  };

  const resetFilters = () => {
    setDateRange({ start: '', end: '' });
    setSelectedType('all');
    setSelectedMonth('all');
  };

  const handleEditClick = (transaction) => {
    setEditingTransaction({
      ...transaction,
      amount: transaction.amount.toString()
    });
  };

  const handleSaveEdit = async () => {
    try {
      if (!editingTransaction.category || !editingTransaction.amount) {
        alert('Category and amount are required');
        return;
      }

      const updatedTransaction = {
        ...editingTransaction,
        amount: parseFloat(editingTransaction.amount)
      };

      await transactionService.updateTransaction(
        editingTransaction._id, 
        updatedTransaction
      );

      setTransactions(transactions.map(t => 
        t._id === editingTransaction._id ? updatedTransaction : t
      ));
      setEditingTransaction(null);
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Failed to update transaction');
    }
  };

  const handleDelete = async (transactionId) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await transactionService.deleteTransaction(transactionId);
        setTransactions(transactions.filter(t => t._id !== transactionId));
        setFilteredTransactions(filteredTransactions.filter(t => t._id !== transactionId));
      } catch (error) {
        console.error('Error deleting transaction:', error);
        alert('Failed to delete transaction');
      }
    }
  };

  return (
    <div className="container" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", paddingTop: "20px" }}>
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
            <button
                  onClick={() => navigate(-1)}
                  className="btn btn-link text-decoration-none p-0 mb-3"
                  style={{ color: "rgb(51, 185, 119)" }}
                >
                  ← Back
                </button>
              <div className="d-flex justify-content-between align-items-center mb-4">
                
                <h2 className="card-title h5 mb-0">All Transactions</h2>

                <button
  className={`btn btn-sm d-flex align-items-center gap-2 ${
    isFilterVisible 
      ? 'custom-filter-active shadow-sm' 
      : 'custom-filter'
  }`}
  onClick={() => setIsFilterVisible(!isFilterVisible)}
  style={{
    borderRadius: '20px',
    padding: '8px 16px',
    fontSize: '0.875rem',
    border: isFilterVisible ? 'none' : '1px solid #e0e0e0',
    transition: 'all 0.2s ease',
  }}
>
  <i className={`bi bi-funnel${isFilterVisible ? '-fill' : ''}`}></i>
  Filters
</button>
              </div>

              {isFilterVisible && (
                <div className="mb-4">
                  <div className="p-3 bg-white rounded-3 shadow-sm border">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label small text-muted">Date Range</label>
                        <div className="d-flex gap-2">
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                            style={{ fontSize: '0.9rem' }}
                          />
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                            style={{ fontSize: '0.9rem' }}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small text-muted">Type</label>
                        <select
                          className="form-select form-select-sm"
                          value={selectedType}
                          onChange={(e) => setSelectedType(e.target.value)}
                          style={{ fontSize: '0.9rem' }}
                        >
                          <option value="all">All Types</option>
                          <option value="income">Income</option>
                          <option value="expense">Expense</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small text-muted">Month</label>
                        <select
                          className="form-select form-select-sm"
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(e.target.value)}
                          style={{ fontSize: '0.9rem' }}
                        >
                          <option value="all">All Months</option>
                          {months.map(month => (
                            <option key={month.value} value={month.value}>
                              {month.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {(dateRange.start || dateRange.end || selectedType !== 'all' || selectedMonth !== 'all') && (
                      <div className="mt-3 text-end">
                        <button
                          className="btn btn-link btn-sm text-danger text-decoration-none"
                          onClick={resetFilters}
                        >
                          Clear all filters
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {loading ? (
                <Spinner />
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : filteredTransactions.length === 0 ? (
                <div className="text-center text-muted p-4">
                  <div className="mb-2">📭</div>
                  No transactions found for the selected filters.
                </div>
              ) : (
                <div className="list-group">
                  {filteredTransactions.map((transaction) => (
                    <div
                      key={transaction._id}
                      className="list-group-item d-flex justify-content-between align-items-center border-0 mb-2 rounded-3 shadow-sm"
                    >
                      {editingTransaction?._id === transaction._id ? (
                        <div className="w-100">
                          <div className="mb-2">
                            <select
                              className="form-select mb-2"
                              value={editingTransaction.type}
                              onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                type: e.target.value
                              })}
                            >
                              <option value="expense">Expense</option>
                              <option value="income">Income</option>
                            </select>
                            
                            <input
                              type="text"
                              className="form-control mb-2"
                              placeholder="Category"
                              value={editingTransaction.category}
                              onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                category: e.target.value
                              })}
                            />
                            
                            <input
                              type="number"
                              className="form-control mb-2"
                              placeholder="Amount"
                              value={editingTransaction.amount}
                              onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                amount: e.target.value
                              })}
                            />
                            
                            <textarea
                              className="form-control mb-2"
                              placeholder="Description (optional)"
                              value={editingTransaction.description || ''}
                              onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                description: e.target.value
                              })}
                            />
                            
                            <div className="d-flex justify-content-end gap-2">
                              <button 
                                className="btn btn-secondary btn-sm"
                                onClick={() => setEditingTransaction(null)}
                              >
                                Cancel
                              </button>
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={handleSaveEdit}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div>
                            <h6 className="mb-0">{transaction.category}</h6>
                            <small className="text-muted">
                              {new Date(transaction.date).toLocaleDateString()}
                            </small>
                            {transaction.description && (
                              <p className="mb-0 text-muted small">
                                {transaction.description}
                              </p>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            <span
                              className={`fw-bold ${
                                transaction.type === "expense" ? "text-danger" : "text-success"
                              }`}
                            >
                              {transaction.type === "expense"
                                ? `- ₹${transaction.amount.toFixed(2)}`
                                : `+ ₹${transaction.amount.toFixed(2)}`}
                            </span>
                            <div className="btn-group">
                              <button 
                                className="btn btn-link btn-sm p-0 text-muted"
                                onClick={() => handleEditClick(transaction)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button 
                                className="btn btn-link btn-sm p-0 text-danger ms-2"
                                onClick={() => handleDelete(transaction._id)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TransactionsPage;
